import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import type { TopicSourceType } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/source-badge/types";
import {
    OTHERS_NODE_ID,
    TOP_DOMAINS_DISPLAYED,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/domains-map/consts";
import type { ITreemapNode } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/domains-map/types";
import type {
    ISourceDomain,
    ISourceDomainResponse,
    ISourceTopic,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/types";

import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { formatTopicToId } from "../utils";

export const transformData = (data: ISourceDomainResponse): ITreemapNode[] => {
    try {
        const allTopicsItem: ISourceTopic = {
            ...data.aggregated,
            topicName: ALL_TOPICS_NAME,
        };
        const topics = [allTopicsItem, ...data.topics];
        return topics.map((topic: ISourceTopic) => {
            const domainNodes: ITreemapNode[] = (topic.domains ?? [])
                .map((domain: ISourceDomain) => ({
                    id: formatTopicToId(`${topic.topicName}-${domain.domain}`),
                    name: domain.domain ?? "",
                    value: domain.sourceAuthority ?? 0,
                    customData: {
                        topicName: topic.topicName ?? "",
                        iconUrl: domain.iconUrl ?? "",
                        sourceCategory: domain.sourceCategory as TopicSourceType,
                        urlCount: domain.urlCount,
                    },
                }))
                .sort((a, b) => b.value - a.value);

            const topDomains = domainNodes.slice(0, TOP_DOMAINS_DISPLAYED);
            const otherDomains = domainNodes.slice(TOP_DOMAINS_DISPLAYED);
            const smallestTopDomain = Math.min(...topDomains.map((n) => n.value));
            const topicChildrenNodes = [
                ...topDomains,
                ...(otherDomains.length
                    ? [
                          {
                              id: `${formatTopicToId(topic.topicName)}-${OTHERS_NODE_ID}`,
                              name: translate("llm.sources_report.source_domains.others"),
                              value: smallestTopDomain - Number.EPSILON * smallestTopDomain,
                              children: otherDomains,
                              customData: {
                                  totalDomains: topic.totalDomains - TOP_DOMAINS_DISPLAYED,
                              },
                          },
                      ]
                    : []),
            ];

            return {
                id: formatTopicToId(topic.topicName),
                name: undefined,
                value: 1,
                children: topicChildrenNodes,
            };
        });
    } catch (error) {
        Sentry.errorTracker({
            message: "Error transforming domains map data",
            e: error,
            level: "error",
        });
        return [];
    }
};
